import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function PrimaNotaAnagraficaPage() {
  // Quando montata sotto la sezione ACQUISTI, nasconde la colonna "N° FATT." e il bottone
  // "📄 Fatture". Le azioni ✏ / 🗑 e il bottone "Unisci duplicati" sono sempre disponibili.
  const isAcquisti = typeof window !== "undefined" && /acquisti/i.test(window.location.pathname || "");
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [dal, setDal] = useState("");
  const [al, setAl] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [duplicati, setDuplicati] = useState(null); // { total_groups, total_duplicates, total_anagrafica }
  const [fattureFor, setFattureFor] = useState(null);
  const [loadingFatture, setLoadingFatture] = useState(false);
  const [selected, setSelected] = useState(new Set()); // pk dei fornitori selezionati
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sortBy, setSortBy] = useState("ragione_sociale"); // "ragione_sociale" | "totale_fatture" | "n_fatture"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  // Toggle: click stessa colonna → inverte direzione. Click altra colonna → default per campo
  // (ragione_sociale=asc, totale/n_fatture=desc).
  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir(col === "ragione_sociale" ? "asc" : "desc");
    }
  };

  const sortedList = (() => {
    const arr = [...list];
    const dirMul = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (sortBy === "ragione_sociale") {
        const va = (a.ragione_sociale || "").toUpperCase();
        const vb = (b.ragione_sociale || "").toUpperCase();
        return va.localeCompare(vb, "it") * dirMul;
      }
      const va = Number(a[sortBy] || 0);
      const vb = Number(b[sortBy] || 0);
      return (va - vb) * dirMul;
    });
    return arr;
  })();

  const sortIcon = (col) => {
    if (sortBy !== col) return <span style={{ opacity: 0.35, marginLeft: 3 }}>⇅</span>;
    return <span style={{ marginLeft: 3, color: "#ffe082" }}>{sortDir === "asc" ? "▲" : "▼"}</span>;
  };

  const carica = async () => {
    setLoading(true);
    try {
      const params = { limit: 1000 };
      if (search) params.search = search;
      if (dal) params.dal = dal;
      if (al) params.al = al;
      const r = await api.get("/pn/anagrafica", { params });
      setList(r.data.data || []);
      setSelected(new Set()); // reset selezione dopo refresh
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  const caricaDuplicati = async () => {
    try {
      const r = await api.get("/pn/anagrafica/duplicati");
      setDuplicati(r.data);
    } catch { setDuplicati(null); }
  };
  useEffect(() => { carica(); caricaDuplicati(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(carica, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, dal, al]);
  // Reset selezione al cambio ordinamento (gli indici cambiano)
  useEffect(() => { setSelected(new Set()); }, [sortBy, sortDir]);

  // Selezione multipla (per indice di riga, perché alcuni fornitori duplicati
  // possono condividere lo stesso primary_key)
  const toggleSel = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const toggleSelAll = () => {
    setSelected((prev) => {
      if (prev.size === sortedList.length && sortedList.length > 0) return new Set();
      return new Set(sortedList.map((_, i) => i));
    });
  };
  const cancellaSelezionati = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Eliminare ${selected.size} fornitori selezionati?\n\nLe fatture eventualmente collegate restano nel database (id_cliente invariato).`)) return;
    setBulkBusy(true);
    const indici = Array.from(selected).sort((a, b) => a - b);
    const errors = [];
    let ok = 0;
    // Sequenziale: ogni DELETE /pn/anagrafica/{pk} è un delete_one, quindi
    // se più righe condividono lo stesso primary_key vengono cancellate una alla volta
    for (const idx of indici) {
      const a = sortedList[idx];
      if (!a) continue;
      try {
        await api.delete(`/pn/anagrafica/${a.primary_key}`);
        ok++;
      } catch (e) {
        errors.push(`${a.ragione_sociale}: ${e.response?.data?.detail || e.message}`);
      }
    }
    // Refresh completo della lista dal backend (più sicuro con duplicati di pk)
    await carica();
    setSelected(new Set());
    caricaDuplicati();
    setBulkBusy(false);
    if (errors.length > 0) {
      alert(`Eliminati: ${ok}\nErrori: ${errors.length}\n\n${errors.slice(0, 5).join("\n")}${errors.length > 5 ? "\n…" : ""}`);
    }
  };

  const mostraFatture = async (a) => {
    setLoadingFatture(true);
    try {
      const r = await api.get(`/pn/anagrafica/${a.primary_key}/fatture`);
      setFattureFor(r.data);
    } catch (e) {
      alert("Errore caricamento fatture: " + (e.response?.data?.detail || e.message));
    } finally { setLoadingFatture(false); }
  };

  const apriNuovo = () => setEditing({ _isNew: true, ragione_sociale: "", piva: "", indirizzo: "", cap: "", citta: "", prov: "", nazione: "ITALIA", email: "" });
  const apriEdit = (a) => setEditing({ ...a });

  const elimina = async (a) => {
    if (!window.confirm(`Eliminare il fornitore "${a.ragione_sociale}"?`)) return;
    try {
      await api.delete(`/pn/anagrafica/${a.primary_key}`);
      // Aggiornamento ottimistico: tolgo la riga dalla lista subito
      setList((prev) => prev.filter((x) => x.primary_key !== a.primary_key));
      // Refresh asincrono del banner duplicati (non blocca la UI)
      caricaDuplicati();
    } catch (e) {
      alert(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const hasDuplicati = duplicati && duplicati.total_groups > 0;

  return (
    <div>
      <div className="sw-page-title">
        <h1>🏢 Anagrafica Fornitori</h1>
        <button onClick={apriNuovo} data-testid="pn-anag-new"
          style={{ padding: "6px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ Nuovo fornitore</button>
      </div>

      <div style={{ margin: "8px 20px", padding: 10, background: "white", border: "1px solid #ccc", display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 260px" }}>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>Cerca ragione sociale, P.IVA, città</div>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} data-testid="pn-anag-search"
            placeholder="Digita per filtrare..."
            style={{ width: "100%", padding: "4px 8px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>Fatt. dal</div>
          <input type="date" value={dal} onChange={e => setDal(e.target.value)} data-testid="pn-anag-dal"
            style={{ padding: "3px 6px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 3 }}>Fatt. al</div>
          <input type="date" value={al} onChange={e => setAl(e.target.value)} data-testid="pn-anag-al"
            style={{ padding: "3px 6px", border: "1px solid #ccc", fontSize: 12 }} />
        </div>
        {(dal || al) && (
          <button onClick={() => { setDal(""); setAl(""); }} data-testid="pn-anag-clear-dates"
            style={{ padding: "5px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            ✕ Periodo
          </button>
        )}
        <button onClick={carica} disabled={loading}
          style={{ padding: "6px 16px", background: "#00695c", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{loading ? "..." : "↻ Aggiorna"}</button>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#444" }}>
          <b>{list.length}</b> fornitori
        </div>
      </div>

      {hasDuplicati && (
        <div style={{ margin: "0 20px 8px", padding: 10, background: "#fff3e0", border: "2px solid #ef6c00", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="pn-anag-dup-banner">
          <span style={{ fontSize: 14, fontWeight: 800, color: "#bf360c" }}>⚠ Trovati {duplicati.total_groups} gruppi di duplicati</span>
          <span style={{ fontSize: 12, color: "#bf360c" }}>(da rimuovere: <b>{duplicati.total_duplicates}</b> su <b>{duplicati.total_anagrafica}</b> totali)</span>
        </div>
      )}

      {/* Barra azioni di massa */}
      <div style={{ margin: "0 20px 8px", padding: 8, background: selected.size > 0 ? "#fff8e1" : "#fafafa", border: `1px solid ${selected.size > 0 ? "#ffb300" : "#ddd"}`, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#444" }}>
          <b>{selected.size}</b> fornitori selezionati
        </span>
        <button onClick={toggleSelAll} disabled={sortedList.length === 0} data-testid="pn-anag-sel-all"
          style={{ padding: "4px 12px", background: "white", color: "#1565c0", border: "1px solid #1565c0", fontSize: 11, fontWeight: 700, cursor: sortedList.length === 0 ? "not-allowed" : "pointer" }}>
          {selected.size === sortedList.length && sortedList.length > 0 ? "✕ Deseleziona tutti" : "✓ Seleziona tutti"}
        </button>
        <span style={{ flex: 1 }} />
        <button onClick={cancellaSelezionati} disabled={selected.size === 0 || bulkBusy} data-testid="pn-anag-del-selected"
          style={{ padding: "6px 16px", background: selected.size === 0 ? "#eee" : "#c62828", color: selected.size === 0 ? "#999" : "white", border: "none", fontSize: 12, fontWeight: 800, cursor: (selected.size === 0 || bulkBusy) ? "not-allowed" : "pointer" }}>
          {bulkBusy ? "Eliminazione…" : `🗑 Cancella selezionati (${selected.size})`}
        </button>
      </div>

      <div style={{ margin: "0 20px 20px", background: "white", border: "1px solid #ccc", maxHeight: "70vh", overflow: "auto" }}>
        <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
          <thead style={{ position: "sticky", top: 0, background: "#37474f", color: "white", zIndex: 1 }}>
            <tr>
              <th style={{ width: 28, textAlign: "center" }}>
                <input type="checkbox" data-testid="pn-anag-sel-all-cb"
                  checked={sortedList.length > 0 && selected.size === sortedList.length}
                  ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < sortedList.length; }}
                  onChange={toggleSelAll} />
              </th>
              <th style={{ width: 32 }}>#</th>
              <th style={{ minWidth: 280, cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("ragione_sociale")} data-testid="pn-anag-sort-rs">
                RAGIONE SOCIALE{sortIcon("ragione_sociale")}
              </th>
              <th style={{ width: 130 }}>P.IVA</th>
              <th style={{ minWidth: 180 }}>INDIRIZZO</th>
              <th style={{ width: 70 }}>CAP</th>
              <th style={{ minWidth: 140 }}>CITTÀ</th>
              <th style={{ width: 50 }}>PROV</th>
              <th style={{ minWidth: 190 }}>EMAIL</th>
              {!isAcquisti && (
                <th style={{ width: 70, textAlign: "right", cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("n_fatture")} data-testid="pn-anag-sort-n">
                  N° FATT.{sortIcon("n_fatture")}
                </th>
              )}
              {!isAcquisti && (
                <th style={{ width: 100, textAlign: "right", cursor: "pointer", userSelect: "none" }} onClick={() => toggleSort("totale_fatture")} data-testid="pn-anag-sort-tot">
                  TOTALE €{sortIcon("totale_fatture")}
                </th>
              )}
              <th style={{ width: 150, textAlign: "center" }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {sortedList.length === 0 && <tr><td colSpan={isAcquisti ? 10 : 12} style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessun fornitore.</td></tr>}
            {sortedList.map((a, i) => (
              <tr key={`${a.primary_key}-${i}`} data-testid={`pn-anag-row-${i}`} style={{ background: selected.has(i) ? "#fffde7" : undefined }}>
                <td style={{ textAlign: "center" }}>
                  <input type="checkbox" data-testid={`pn-anag-sel-${i}`}
                    checked={selected.has(i)}
                    onChange={() => toggleSel(i)} />
                </td>
                <td style={{ textAlign: "center", color: "#888", fontFamily: "monospace" }}>{i+1}</td>
                <td style={{ fontWeight: 600, textTransform: "uppercase" }}>{a.ragione_sociale}</td>
                <td style={{ fontFamily: "monospace", fontSize: 10 }}>{a.piva || "—"}</td>
                <td style={{ fontSize: 10, color: "#555" }}>{a.indirizzo || <span style={{ color: "#bbb" }}>—</span>}</td>
                <td style={{ fontFamily: "monospace", fontSize: 10 }}>{a.cap || "—"}</td>
                <td>{a.citta || "—"}</td>
                <td style={{ textAlign: "center", fontFamily: "monospace", fontSize: 10 }}>{a.prov || "—"}</td>
                <td style={{ fontSize: 10 }} data-testid={`pn-anag-email-${i}`}>
                  {a.email || (a.emails && a.emails[0]) || <span style={{ color: "#bbb" }}>—</span>}
                </td>
                {!isAcquisti && (
                  <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700, color: (a.n_fatture || 0) > 0 ? "#1b5e20" : "#bdbdbd" }} data-testid={`pn-anag-nfatt-${i}`}>
                    {a.n_fatture ?? 0}
                  </td>
                )}
                {!isAcquisti && (
                  <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700, color: (a.totale_fatture || 0) > 0 ? "#0d47a1" : "#bdbdbd" }} data-testid={`pn-anag-tot-${i}`}>
                    {(a.totale_fatture || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                )}
                <td style={{ textAlign: "center" }}>
                  {!isAcquisti && (
                    <button onClick={() => mostraFatture(a)} disabled={loadingFatture || (a.n_fatture || 0) === 0}
                      title={(a.n_fatture || 0) === 0 ? "Nessuna fattura collegata" : "Mostra fatture collegate"}
                      data-testid={`pn-anag-fatture-${i}`}
                      style={{ padding: "2px 8px", background: (a.n_fatture || 0) === 0 ? "#eee" : "#00695c", color: (a.n_fatture || 0) === 0 ? "#999" : "white", border: "none", fontSize: 10, fontWeight: 700, cursor: (a.n_fatture || 0) === 0 ? "not-allowed" : "pointer", marginRight: 3 }}>
                      📄 Fatture
                    </button>
                  )}
                  <button onClick={() => apriEdit(a)} data-testid={`pn-anag-edit-${i}`}
                    style={{ padding: "2px 8px", background: "#1565c0", color: "white", border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", marginRight: 3 }}>✏</button>
                  <button onClick={() => elimina(a)} data-testid={`pn-anag-del-${i}`}
                    style={{ padding: "2px 8px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
          {!isAcquisti && sortedList.length > 0 && (
            <tfoot>
              <tr style={{ background: "#37474f", color: "white", fontWeight: 800, position: "sticky", bottom: 0 }}>
                <td colSpan={9} style={{ padding: "6px 8px", textAlign: "right" }}>
                  {(dal || al) ? `TOTALE PERIODO${dal ? " dal " + dal.split("-").reverse().join("/") : ""}${al ? " al " + al.split("-").reverse().join("/") : ""}` : "TOTALE GENERALE"}
                </td>
                <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", padding: "6px 8px" }} data-testid="pn-anag-foot-nfatt">
                  {sortedList.reduce((s, a) => s + (a.n_fatture || 0), 0)}
                </td>
                <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", padding: "6px 8px", color: "#c5e1a5" }} data-testid="pn-anag-foot-tot">
                  {sortedList.reduce((s, a) => s + (a.totale_fatture || 0), 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {editing && <AnagraficaEditor a={editing} onClose={() => { setEditing(null); carica(); }} />}
      {fattureFor && <FattureFornitoreModal data={fattureFor} onClose={() => setFattureFor(null)} />}
    </div>
  );
}

function AnagraficaEditor({ a, onClose }) {
  const [doc, setDoc] = useState(a);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const isNew = !!doc._isNew;
  const set = (k, v) => setDoc(d => ({ ...d, [k]: v }));

  const salva = async () => {
    if (!doc.ragione_sociale?.trim()) { setErr("Ragione sociale obbligatoria"); return; }
    setSaving(true); setErr(null);
    try {
      const body = {
        ragione_sociale: doc.ragione_sociale,
        piva: doc.piva || null,
        citta: doc.citta || null,
        prov: doc.prov || null,
        cap: doc.cap || null,
        indirizzo: doc.indirizzo || null,
        nazione: doc.nazione || null,
        email: (doc.email || "").trim() || null,
      };
      if (isNew) {
        await api.post("/pn/anagrafica", body);
      } else {
        await api.put(`/pn/anagrafica/${doc.primary_key}`, body);
      }
      onClose();
    } catch (e) { setErr(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} data-testid="pn-anag-editor"
        style={{ background: "white", width: 600, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#1565c0", color: "white", padding: "6px 12px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>{isNew ? "+ Nuovo fornitore" : `✏ ${doc.ragione_sociale}`}</span>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} style={{ padding: "3px 10px", background: "white", color: "#1565c0", border: "none", fontWeight: 700, cursor: "pointer" }}>✕ Chiudi</button>
        </div>
        {err && <div style={{ padding: 8, background: "#fff0f0", color: "#c00", fontSize: 12 }}>{err}</div>}
        <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8, flex: 1, overflow: "auto" }}>
          <Field label="RAG. SOC. *" value={doc.ragione_sociale || ""} onChange={v => set("ragione_sociale", v)} testid="pn-anag-edit-rs" />
          <Field label="P.IVA" value={doc.piva || ""} onChange={v => set("piva", v)} testid="pn-anag-edit-piva" />
          <Field label="INDIRIZZO" value={doc.indirizzo || ""} onChange={v => set("indirizzo", v)} testid="pn-anag-edit-indirizzo" />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ width: 100 }}>
              <Field label="CAP" value={doc.cap || ""} onChange={v => set("cap", v)} testid="pn-anag-edit-cap" />
            </div>
            <div style={{ flex: 1 }}>
              <Field label="CITTÀ" value={doc.citta || ""} onChange={v => set("citta", v)} testid="pn-anag-edit-citta" />
            </div>
            <div style={{ width: 70 }}>
              <Field label="PROV" value={doc.prov || ""} onChange={v => set("prov", v)} testid="pn-anag-edit-prov" />
            </div>
          </div>
          <Field label="NAZIONE" value={doc.nazione || ""} onChange={v => set("nazione", v)} testid="pn-anag-edit-nazione" />
          <Field label="EMAIL (usata per l'invio ordini)" value={doc.email || ""} onChange={v => set("email", v)} testid="pn-anag-edit-email" />
        </div>
        <div style={{ padding: 8, background: "#fafafa", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "5px 14px", background: "white", border: "1px solid #555", cursor: "pointer" }}>Annulla</button>
          <button onClick={salva} disabled={saving} data-testid="pn-anag-save"
            style={{ padding: "5px 14px", background: "#1565c0", color: "white", border: "none", fontWeight: 700, cursor: "pointer" }}>
            {saving ? "..." : "💾 Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testid }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
    <label style={{ fontSize: 11, fontWeight: 700, color: "#37474f" }}>{label}</label>
    <input type="text" value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
      style={{ padding: "5px 8px", border: "1px solid #888", fontSize: 12 }} />
  </div>
);

function FattureFornitoreModal({ data, onClose }) {
  const f = data.fornitore || {};
  const rows = data.data || [];
  const fmtNum = (n) => (n == null ? "" : Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const tipoLabel = (id) => (id === 1 ? "FATT" : id === 2 ? "N.C." : id === 3 ? "EXTRA" : "—");
  return (
    <div onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 30, overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} data-testid="pn-anag-fatture-modal"
           style={{ background: "white", width: "100%", maxWidth: 1100, maxHeight: "90vh", display: "flex", flexDirection: "column", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ background: "#00695c", color: "white", padding: "10px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 800, textTransform: "uppercase" }}>📄 Fatture collegate · {f.ragione_sociale}</div>
            <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>
              P.IVA <span style={{ fontFamily: "monospace" }}>{f.piva || "—"}</span> · <b>{data.count}</b> fattur{data.count === 1 ? "a" : "e"} · Totale €&nbsp;{fmtNum(data.totale)}
            </div>
          </div>
          <button onClick={onClose} data-testid="pn-anag-fatture-close"
                  style={{ padding: "4px 12px", background: "white", color: "#00695c", border: "none", fontWeight: 700, cursor: "pointer" }}>✕ Chiudi</button>
        </div>
        <div style={{ flex: 1, overflow: "auto" }}>
          {rows.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessuna fattura collegata.</div>
          ) : (
            <table className="sw-table sw-table-compact" style={{ width: "100%", fontSize: 11 }}>
              <thead style={{ position: "sticky", top: 0, background: "#37474f", color: "white", zIndex: 1 }}>
                <tr>
                  <th style={{ width: 60, textAlign: "center" }}>TIPO</th>
                  <th style={{ width: 110 }}>PROTOCOLLO</th>
                  <th style={{ width: 140 }}>N° FATTURA</th>
                  <th style={{ width: 100 }}>DATA FATT.</th>
                  <th style={{ width: 100 }}>DATA REG.</th>
                  <th style={{ width: 110, textAlign: "right" }}>IMPONIBILE</th>
                  <th style={{ width: 90, textAlign: "right" }}>IVA</th>
                  <th style={{ width: 110, textAlign: "right" }}>TOTALE</th>
                  <th style={{ width: 50, textAlign: "center" }}>SGN</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id_doc ?? i} data-testid={`pn-anag-fatture-row-${i}`} style={{ background: i % 2 ? "#fafafa" : "white" }}>
                    <td style={{ textAlign: "center", fontFamily: "monospace", fontWeight: 700, color: r.prot_tipo_id === 2 ? "#c62828" : (r.prot_tipo_id === 3 ? "#6a1b9a" : "#1b5e20") }}>{tipoLabel(r.prot_tipo_id)}</td>
                    <td style={{ fontFamily: "monospace" }}>{r.prot_anno || "—"}/{r.prot_numero ?? "—"}</td>
                    <td style={{ fontFamily: "monospace" }}>{r.numero_fattura || "—"}</td>
                    <td>{r.data_fattura || "—"}</td>
                    <td>{r.data_registrazione || "—"}</td>
                    <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>{fmtNum(r.imponibile)}</td>
                    <td style={{ textAlign: "right", fontFamily: "Consolas, monospace" }}>{fmtNum(r.iva)}</td>
                    <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 700 }}>{fmtNum(r.totale)}</td>
                    <td style={{ textAlign: "center", fontWeight: 700, color: r.segno === "-" ? "#c62828" : "#1b5e20" }}>{r.segno || "+"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#eceff1", fontWeight: 800 }}>
                  <td colSpan={7} style={{ padding: "6px 8px", textAlign: "right" }}>TOTALE</td>
                  <td style={{ textAlign: "right", fontFamily: "Consolas, monospace", padding: "6px 8px" }}>€&nbsp;{fmtNum(data.totale)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
